import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, Mail, ArrowRight, Sparkles, MoreHorizontal, Flame, User, Edit, Zap, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useHotLeads } from "@/hooks/useDashboard";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDistanceToNow } from "date-fns";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScheduleCallDialog } from "@/components/calls/ScheduleCallDialog";
import { SendWhatsAppDialog } from "@/components/outreach/SendWhatsAppDialog";
import { toast } from "sonner";

const statusConfig = {
  new: { label: "New", className: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  attempted_contact: { label: "Attempted", className: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30" },
  engaged: { label: "Engaged", className: "bg-purple-500/15 text-purple-400 border-purple-500/30" },
  qualified: { label: "Qualified", className: "bg-success/15 text-success border-success/30" },
  unqualified: { label: "Unqualified", className: "bg-muted text-muted-foreground border-border" },
  converted: { label: "Converted", className: "bg-success/15 text-success border-success/30" },
};

function ScoreIndicator({ score }: { score: number }) {
  const getScoreColor = (score: number) => {
    if (score >= 90) return "from-success to-success/70";
    if (score >= 70) return "from-primary to-primary/70";
    if (score >= 50) return "from-warning to-warning/70";
    return "from-muted-foreground to-muted-foreground/70";
  };

  return (
    <div className="flex items-center gap-3">
      <div className="relative w-20 h-2 rounded-full bg-muted overflow-hidden">
        <div
          className={cn(
            "absolute inset-y-0 left-0 rounded-full bg-gradient-to-r transition-all duration-500",
            getScoreColor(score)
          )}
          style={{ width: `${score}%` }}
        />
        {/* Shimmer effect */}
        <div 
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer"
          style={{ width: `${score}%` }}
        />
      </div>
      <span className={cn(
        "text-sm font-bold tabular-nums",
        score >= 90 && "text-success",
        score >= 70 && score < 90 && "text-primary",
        score >= 50 && score < 70 && "text-warning",
        score < 50 && "text-muted-foreground"
      )}>
        {score}
      </span>
    </div>
  );
}

export function HotLeadsTable() {
  const { data: leads, isLoading } = useHotLeads();
  const navigate = useNavigate();
  const [scheduleCallOpen, setScheduleCallOpen] = useState(false);
  const [whatsAppOpen, setWhatsAppOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<any>(null);

  const handlePhoneClick = (lead: any) => {
    setSelectedLead(lead);
    setScheduleCallOpen(true);
  };

  const handleMailClick = (lead: any) => {
    if (lead.phone) {
      setSelectedLead(lead);
      setWhatsAppOpen(true);
    } else if (lead.email) {
      window.location.href = `mailto:${lead.email}`;
    } else {
      toast.error("No contact information available");
    }
  };

  const handleViewLead = (leadId: string) => {
    navigate(`/app/leads?id=${leadId}`);
  };

  const handleScoreLead = (leadId: string) => {
    toast.info("AI scoring in progress...");
    // This would trigger AI scoring - for now just show toast
    setTimeout(() => toast.success("Lead scored successfully"), 1500);
  };

  const handleViewAll = () => {
    navigate("/app/leads");
  };

  if (isLoading) {
    return (
      <div className="glass card-gradient rounded-xl overflow-hidden">
        <div className="p-5 border-b border-border/50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-primary/10">
              <Flame className="h-4 w-4 text-primary" />
            </div>
            <h2 className="text-lg font-semibold">Hot Leads</h2>
            <span className="ai-badge">AI Prioritized</span>
          </div>
        </div>
        <div className="p-5 space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (!leads || leads.length === 0) {
    return (
      <div className="glass card-gradient rounded-xl overflow-hidden">
        <div className="p-5 border-b border-border/50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-primary/10">
              <Flame className="h-4 w-4 text-primary" />
            </div>
            <h2 className="text-lg font-semibold">Hot Leads</h2>
            <span className="ai-badge">AI Prioritized</span>
          </div>
        </div>
        <div className="text-center py-12 text-muted-foreground">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted/50 flex items-center justify-center">
            <User className="h-8 w-8 text-muted-foreground/50" />
          </div>
          <p className="font-medium">No leads yet</p>
          <p className="text-sm mt-1 text-muted-foreground/70">Add your first lead to get started.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="glass card-gradient rounded-xl overflow-hidden">
        <div className="p-5 border-b border-border/50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-primary/20 rounded-lg blur-md" />
              <div className="relative p-1.5 rounded-lg bg-primary/10 border border-primary/20">
                <Flame className="h-4 w-4 text-primary" />
              </div>
            </div>
            <h2 className="text-lg font-semibold">Hot Leads</h2>
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20">
              <Sparkles className="h-3 w-3 text-primary" />
              <span className="text-[10px] font-semibold text-primary uppercase tracking-wide">AI Prioritized</span>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-primary gap-1.5 hover:bg-primary/10 group"
            onClick={handleViewAll}
          >
            View All 
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border/50 bg-muted/20">
                <th className="text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-5 py-3">
                  Contact
                </th>
                <th className="text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-5 py-3">
                  AI Score
                </th>
                <th className="text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-5 py-3">
                  Status
                </th>
                <th className="text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-5 py-3">
                  Last Activity
                </th>
                <th className="text-right text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-5 py-3">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/30">
              {leads.map((lead, index) => {
                const status = lead.status || "new";
                const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.new;

                return (
                  <tr 
                    key={lead.id} 
                    className={cn(
                      "hover:bg-muted/30 transition-colors group cursor-pointer",
                      index === 0 && "bg-primary/5"
                    )}
                    onClick={() => handleViewLead(lead.id)}
                  >
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center border border-primary/20">
                          <span className="text-sm font-semibold text-primary">
                            {(lead.first_name?.[0] || "").toUpperCase()}
                            {(lead.last_name?.[0] || "").toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-foreground group-hover:text-primary transition-colors">
                            {lead.first_name} {lead.last_name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {lead.company_name || "No company"}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <ScoreIndicator score={lead.ai_conversion_score || 0} />
                    </td>
                    <td className="px-5 py-4">
                      <span className={cn(
                        "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border",
                        config.className
                      )}>
                        {config.label}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <span className="text-sm text-muted-foreground">
                        {lead.last_activity_at
                          ? formatDistanceToNow(new Date(lead.last_activity_at), { addSuffix: true })
                          : "No activity"}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8 hover:bg-primary/10 hover:text-primary transition-colors"
                          onClick={() => handlePhoneClick(lead)}
                        >
                          <Phone className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8 hover:bg-primary/10 hover:text-primary transition-colors"
                          onClick={() => handleMailClick(lead)}
                        >
                          <Mail className="h-4 w-4" />
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 hover:bg-muted transition-colors"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewLead(lead.id)}>
                              <Edit className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleScoreLead(lead.id)}>
                              <Zap className="h-4 w-4 mr-2" />
                              AI Score
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleMailClick(lead)}>
                              <MessageSquare className="h-4 w-4 mr-2" />
                              Send Message
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Schedule Call Dialog */}
      <ScheduleCallDialog 
        open={scheduleCallOpen} 
        onOpenChange={setScheduleCallOpen}
        preSelectedLeadId={selectedLead?.id}
      />

      {/* WhatsApp Dialog */}
      <SendWhatsAppDialog 
        open={whatsAppOpen} 
        onOpenChange={setWhatsAppOpen}
        leadId={selectedLead?.id}
        leadPhone={selectedLead?.phone}
        leadName={selectedLead ? `${selectedLead.first_name} ${selectedLead.last_name}` : undefined}
      />
    </>
  );
}